import { ArrowUpDown, ChevronDown, Columns3, LayoutGrid, SlidersHorizontal, Table2, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { BROWSE_SORTS, BROWSE_SORT_LABELS, type BrowseSort, type CandidateBrowseRow } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/Checkbox';
import { TableViews } from '../../../../components/data-table/TableViews';
import type { Column } from '../../../../components/data-table/types';
import type { TablePreferences } from '../../../../components/data-table/use-table-preferences';
import { MenuGroup, MenuItem, Popover, usePopover } from '../../../../components/Popover';
import { formatNumber } from '../../../../lib/format';
import { useTableViews } from '../../../../lib/table-views-api';
import { sameFilters } from './browse-address';
import type { FilterChip } from './list-format';
import type { ListLayout } from './use-list-layout';
import styles from './Results.module.css';

const ALL_VIEW = 'All candidates';

interface ResultsToolbarProps {
  readonly total: number | undefined;
  readonly chips: readonly FilterChip[];
  readonly onRemoveChip: (chip: FilterChip) => void;
  readonly filters: Readonly<Record<string, string>>;
  readonly onApplyFilters: (filters: Readonly<Record<string, string>>) => void;
  readonly sort: BrowseSort;
  readonly onSort: (sort: BrowseSort) => void;
  readonly layout: ListLayout;
  readonly onLayout: (layout: ListLayout) => void;
  readonly columns: readonly Column<CandidateBrowseRow>[];
  readonly preferences: TablePreferences;
  /** Tablet and phone: the filters live behind this button. */
  readonly filterCount: number;
  readonly onOpenFilters: () => void;
}

/** The results card's top row (mockup Main.dc.html): the count, the active filters, and how the list shows. */
export function ResultsToolbar(props: ResultsToolbarProps): ReactNode {
  const { total, chips, onRemoveChip, filterCount, onOpenFilters } = props;
  return (
    <div className={styles.toolbar}>
      <span className={styles.total}>{total === undefined ? '—' : `${formatNumber(total)} ${total === 1 ? 'candidate' : 'candidates'}`}</span>
      <span className={styles.filtersButton}>
        <Button variant="outline" size="sm" icon={SlidersHorizontal} aria-haspopup="dialog" onClick={onOpenFilters}>
          {filterCount > 0 ? `Filters (${formatNumber(filterCount)})` : 'Filters'}
        </Button>
      </span>
      {chips.map((chip) => (
        <span key={chip.id} className={styles.chip}>
          {chip.label}
          <button type="button" aria-label={`Remove ${chip.label}`} onClick={() => onRemoveChip(chip)}>
            <X size={13} aria-hidden />
          </button>
        </span>
      ))}
      <span className={styles.grow} />
      <ViewMenu {...props} />
      <SortMenu sort={props.sort} onSort={props.onSort} />
      <LayoutSwitch layout={props.layout} onLayout={props.onLayout} />
      {props.layout === 'table' && <ColumnsMenu columns={props.columns} preferences={props.preferences} />}
    </div>
  );
}

/** "View: …": the saved views (S32 Q28), named for the one on screen. */
function ViewMenu({ filters, onApplyFilters, preferences }: ResultsToolbarProps): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const views = useTableViews('candidates', true);
  const isAll = Object.keys(filters).length === 0;
  const current = isAll ? ALL_VIEW : (views.data?.find((view) => sameFilters(view.state.filters, filters))?.name ?? 'Unsaved filters');
  return (
    <>
      <Button ref={triggerRef} variant="outline" size="sm" aria-haspopup="menu" onClick={toggle}>
        View: {current}
        <ChevronDown size={14} aria-hidden />
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" width={320} label="Saved views">
        <MenuItem
          isSelected={isAll}
          onClick={() => {
            onApplyFilters({});
            close();
          }}
        >
          {ALL_VIEW}
        </MenuItem>
        <TableViews
          config={{ screen: 'candidates', filters, onApply: onApplyFilters }}
          hiddenColumns={[...preferences.hiddenColumns]}
          onApplyColumns={preferences.setHiddenColumns}
          onDone={close}
        />
      </Popover>
    </>
  );
}

function SortMenu({ sort, onSort }: { readonly sort: BrowseSort; readonly onSort: (sort: BrowseSort) => void }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  return (
    <>
      <Button ref={triggerRef} variant="outline" size="sm" icon={ArrowUpDown} aria-haspopup="menu" onClick={toggle}>
        Sort: {BROWSE_SORT_LABELS[sort]}
      </Button>
      <Popover anchor={anchor} onClose={close} align="right" label="Sort by">
        {BROWSE_SORTS.map((option) => (
          <MenuItem
            key={option}
            isSelected={option === sort}
            onClick={() => {
              onSort(option);
              close();
            }}
          >
            {BROWSE_SORT_LABELS[option]}
          </MenuItem>
        ))}
      </Popover>
    </>
  );
}

function LayoutSwitch({ layout, onLayout }: { readonly layout: ListLayout; readonly onLayout: (layout: ListLayout) => void }): ReactNode {
  return (
    <div className={styles.switch} role="group" aria-label="Show as">
      <button type="button" aria-pressed={layout === 'table'} onClick={() => onLayout('table')}>
        <Table2 size={14} aria-hidden />
        Table
      </button>
      <button type="button" aria-pressed={layout === 'cards'} onClick={() => onLayout('cards')}>
        <LayoutGrid size={14} aria-hidden />
        Cards
      </button>
    </div>
  );
}

/** The column chooser the app's tables share (06-qol §3), kept per viewer. */
function ColumnsMenu({ columns, preferences }: { readonly columns: readonly Column<CandidateBrowseRow>[]; readonly preferences: TablePreferences }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  return (
    <>
      <Button ref={triggerRef} variant="outline" size="sm" icon={Columns3} aria-label="Choose columns" title="Columns" aria-haspopup="menu" onClick={toggle} />
      <Popover anchor={anchor} onClose={close} align="right" label="Choose columns">
        <MenuGroup label="Columns">
          {columns.map((column) => {
            const isShown = !preferences.hiddenColumns.has(column.id);
            return (
              <MenuItem
                key={column.id}
                disabled={column.isIdentity === true}
                isSelected={isShown}
                onClick={() => preferences.toggleColumn(column.id)}
                icon={<Checkbox checked={isShown} onChange={() => preferences.toggleColumn(column.id)} ariaLabel={column.header} disabled={column.isIdentity === true} />}
              >
                {column.header}
              </MenuItem>
            );
          })}
        </MenuGroup>
        <MenuItem onClick={preferences.resetColumns}>Reset columns</MenuItem>
      </Popover>
    </>
  );
}
